/**
 * One item, in full: body, payload, where it came from and what came out of
 * it, its tags (add one, approve one), and the human's three verbs — rule out,
 * reopen, mark done. Fetches its own detail because the feed rows carry no
 * subtree.
 */
import { useCallback, useEffect, useState } from 'react';
import FetchLog from './FetchLog';
import TagChip from './TagChip';
import useBoardRefresh from './reports/useBoardRefresh';
import PayloadView from './PayloadView';
import Drawer from '../common/Drawer';
import api from '../../services/api';
import type { BoardroomClosure, BoardroomItem, BoardroomItemDetail, BoardroomItemKind, BoardroomPersona, BoardroomTag } from '../../types';
import { KIND_LABELS, isLookup, kindTone, originLabel, quoteOrigin, sourceUrl, statusLabel } from './kinds';
import AddItemForm, { type NewItem } from './AddItemForm';
import { priceLabel } from './pricing';
import { VerdictBadge } from './ItemCard';
import type { BoardroomVerdictValue } from '../../types';
import { whoMade } from './ItemCard';
import { DemandRowsPanel, TrendPanel, type DemandRow, type TrendPayload } from './reports/LookupPanels';
import PromoteIdea from './PromoteIdea';

const DIMENSIONS = ['INDUSTRY', 'NICHE', 'BUYER', 'PRODUCT_TYPE', 'MODALITY', 'SCALE', 'TYPE', 'ENTITY_KIND'];

/** A receipt in words: the search it was, the page it read, the numbers it pulled. */
export function receiptLabel(r: { kind: BoardroomItemKind; title: string; payload: Record<string, unknown> }): string {
  const p = r.payload || {};
  if (r.kind === 'LOOKUP_SERP' && typeof p.keyword === 'string') return `the search "${p.keyword}"`;
  if (r.kind === 'LOOKUP_PAGE' && typeof p.url === 'string') return `the page ${String(p.url).replace(/^https?:\/\//, '')}`;
  if (r.kind === 'LOOKUP_DEMAND' && Array.isArray(p.seeds)) return `Google Ads numbers for ${(p.seeds as string[]).slice(0, 3).join(', ')}`;
  if (r.kind === 'LOOKUP_TRENDS' && typeof p.keyword === 'string') return `Google Trends for "${p.keyword}"`;
  if (r.kind === 'LOOKUP_NEWS' && typeof p.keyword === 'string') return `the news about "${p.keyword}"`;
  if (r.kind === 'LOOKUP_SOCIAL' && typeof p.keyword === 'string') return `posts about "${p.keyword}"`;
  if (r.kind === 'LOOKUP_CONSOLE' && typeof p.keyword === 'string') return `a console search for "${p.keyword}"`;
  if (r.kind === 'EVIDENCE') return `the evidence "${r.title}"`;
  return r.title;
}

export default function ItemDrawer({
  boardroomId,
  item,
  personas,
  onClose,
  onChanged,
  onOpen,
  onProfile,
  trail = [],
  onBack,
  canPublish = false,
}: {
  boardroomId: number;
  item: BoardroomItem;
  personas: Map<number, BoardroomPersona>;
  /** Whether this account holds Publish — what an idea's subject belongs to. */
  canPublish?: boolean;
  onClose: () => void;
  /** The item after a mutation, so the feed can upsert without waiting for the socket. */
  onChanged: (item: BoardroomItem) => void;
  onOpen: (item: BoardroomItem) => void;
  /** The profile page for a company, product or type, when the page has one. */
  onProfile?: (itemId: number) => void;
  /** Where you have been inside the drawer, oldest first, this item last. */
  trail?: BoardroomItem[];
  onBack?: () => void;
}) {
  const [detail, setDetail] = useState<BoardroomItemDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dimension, setDimension] = useState('INDUSTRY');
  const [value, setValue] = useState('');

  const load = useCallback(async () => {
    try {
      setDetail(await api.getBoardroomItem(boardroomId, item.id));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the item');
    }
  }, [boardroomId, item.id]);

  // LIVE WHILE OPEN. A lookup moving from queued to read, a tag the sorter
  // places, a verdict filed against it: all landed while the drawer showed
  // what it read when it opened.
  const fresh = useBoardRefresh(boardroomId);
  useEffect(() => {
    load();
  }, [load, fresh]);

  /** Your own ruling: a VERDICT item linked VERIFIES to this one, same as the
   *  skeptic files. The badge on the card derives from it. */
  const rule = async (verdict: BoardroomVerdictValue) => {
    try {
      const v = await api.createBoardroomItem(boardroomId, {
        kind: 'VERDICT',
        title: `${verdict}: ${item.title}`.slice(0, 300),
        body: 'Ruled by hand.',
        payload: { verdict },
        parent_id: item.id,
        link_kind: 'VERIFIES',
      });
      const ref = { verdict, item_id: v.id, by: null };
      setDetail(d => (d ? { ...d, verdict: ref } : d));
      onChanged({ ...item, verdict: ref });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not record the verdict');
    }
  };

  const setStatus = async (status: 'OPEN' | 'DONE' | 'KILLED') => {
    try {
      const next = await api.updateBoardroomItem(boardroomId, item.id, { status });
      onChanged(next);
      setDetail(d => (d ? { ...d, ...next } : d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update the item');
    }
  };

  const addTag = async () => {
    const v = value.trim();
    if (!v) return;
    try {
      const tag = await api.addBoardroomTag(boardroomId, item.id, { dimension, value: v });
      setValue('');
      const merge = (tags: BoardroomTag[]) =>
        tags.some(t => t.id === tag.id) ? tags : [...tags, tag];
      setDetail(d => (d ? { ...d, tags: merge(d.tags) } : d));
      onChanged({ ...item, tags: merge(item.tags) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not add the tag');
    }
  };

  const approveTag = async (tag: BoardroomTag) => {
    try {
      const next = await api.approveBoardroomTag(boardroomId, tag.id);
      const swap = (tags: BoardroomTag[]) => tags.map(t => (t.id === next.id ? next : t));
      setDetail(d => (d ? { ...d, tags: swap(d.tags) } : d));
      onChanged({ ...item, tags: swap(item.tags) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not approve the tag');
    }
  };

  const removeTag = async (tag: BoardroomTag) => {
    try {
      await api.deleteBoardroomTag(boardroomId, tag.id);
      const strip = (tags: BoardroomTag[]) => tags.filter(t => t.id !== tag.id);
      setDetail(d => (d ? { ...d, tags: strip(d.tags) } : d));
      onChanged({ ...item, tags: strip(item.tags) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not remove the tag');
    }
  };

  const reopen = async (c: BoardroomClosure) => {
    try {
      await api.reopenBoardroomClosure(boardroomId, item.id, c.what, c.archetype);
      const strip = (cs: BoardroomClosure[]) => cs.filter(x => x.id !== c.id);
      setDetail(d => (d ? { ...d, closures: strip(d.closures ?? []) } : d));
      onChanged({ ...item, closures: strip(item.closures ?? []) });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not ask that question again');
    }
  };

  const d = detail ?? { ...item, children: [], parents: [], receipts: [] };
  const lookup = isLookup(item.kind);
  const url = sourceUrl(d);
  const origin = quoteOrigin(d.payload);
  const before = trail.slice(0, -1);
  const plans = (detail?.children ?? []).filter(c => c.link_kind === 'PRICES' && c.item.status !== 'KILLED');
  // The demand and trends lookups that landed for THIS item — an idea, a
  // product, a type — shown inline so the numbers are one click nearer.
  const landed = (detail?.children ?? []).filter(
    c =>
      c.item.status === 'DONE' &&
      ((c.item.kind === 'LOOKUP_TRENDS' && c.item.payload.trend && typeof c.item.payload.trend === 'object') ||
        (c.item.kind === 'LOOKUP_DEMAND' && Array.isArray(c.item.payload.rows))),
  );
  const panelled =
    d.status === 'DONE' &&
    ((item.kind === 'LOOKUP_TRENDS' && !!d.payload.trend) || (item.kind === 'LOOKUP_DEMAND' && Array.isArray(d.payload.rows)));
  const addPlan = async (body: NewItem) => {
    await api.createBoardroomItem(boardroomId, body);
    await load();
  };

  return (
    <Drawer
      onClose={onClose}
      eyebrow={`${KIND_LABELS[item.kind]} · #${item.id}`}
      title={<span className="text-base">{d.title}</span>}
    >
      {before.length > 0 && onBack && (
        /* ONE WAY BACK, named, the same shape as the room's Back. The drawer
           covers the page, so the room's location bar cannot be reached from
           here; small crumbs of every item visited read as a path, which they
           were not. */
        <nav aria-label="Back in this drawer" className="mb-2 min-w-0" data-testid="drawer-trail">
          <button
            type="button"
            onClick={onBack}
            title={`Back to ${before[before.length - 1].title}`}
            className="flex min-w-0 max-w-full items-center gap-2 rounded-md border border-gray-200 px-2 py-1 text-sm text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-gray-100"
            data-testid="drawer-back"
          >
            <span aria-hidden>←</span>
            <span className="truncate">Back to {before[before.length - 1].title}</span>
          </button>
        </nav>
      )}
      <div className="text-xs text-gray-500 dark:text-gray-400 flex flex-wrap items-center gap-2">
        <span className={`px-1.5 py-0.5 rounded ${kindTone(item.kind)}`}>{KIND_LABELS[item.kind]}</span>
        <span>{whoMade(item, personas)}</span>
        <span>· {statusLabel(item.kind, d.status)}</span>
        {d.worker_kind && d.worker_id && <span>· held by {d.worker_kind === 'PERSONA' ? personas.get(Number(d.worker_id))?.name ?? 'a persona' : 'the lookup loop'}</span>}
        {url && (
          /* THE PAGE ITSELF. A read page's summary is on the board; the page is
             one click away from it, in a new tab. */
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            title={url}
            className="text-indigo-600 dark:text-indigo-400 hover:underline truncate max-w-[16rem]"
            data-testid="item-url"
          >
            {d.source && d.source.via !== 'own' && <span className="text-gray-500 dark:text-gray-400">source: </span>}
            {url.replace(/^https?:\/\//, '')} ↗
          </a>
        )}
        {onProfile && (item.kind === 'COMPANY' || item.kind === 'PRODUCT' || item.kind === 'SOLUTION_TYPE') && (
          <button
            type="button"
            onClick={() => onProfile(item.id)}
            className="ml-auto px-2 py-0.5 rounded border border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20"
            data-testid="open-profile"
          >
            Open profile
          </button>
        )}
      </div>

      {error && (
        <div className="mt-2 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </div>
      )}

      {d.body && (
        <p className="mt-3 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-line">{d.body}</p>
      )}
      {d.error && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{d.error}</p>}
      <FetchLog payload={d.payload} />

      {/* WHAT THE LOOKUP SAYS, as a panel. A person opens a lookup for the
          details, and the details are a trend line and a table, not a dict.
          The raw payload stays below, folded, for the receipt. */}
      {item.kind === 'LOOKUP_TRENDS' && d.status === 'DONE' && !!d.payload.trend && typeof d.payload.trend === 'object' && (
        <section className="mt-3">
          <TrendPanel trend={d.payload.trend as TrendPayload} testId="trend-panel" />
        </section>
      )}
      {item.kind === 'LOOKUP_DEMAND' && d.status === 'DONE' && Array.isArray(d.payload.rows) && (
        <section className="mt-3">
          <DemandRowsPanel rows={d.payload.rows as DemandRow[]} related={(Array.isArray(d.payload.related) ? d.payload.related : []) as DemandRow[]} testId="demand-panel" />
        </section>
      )}
      {landed.length > 0 && (
        <section className="mt-4" data-testid="landed-lookups">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Demand & trends</h4>
          <div className="flex flex-col gap-3">
            {landed.map(c => (
              <div key={c.item.id}>
                <button type="button" onClick={() => onOpen(c.item)} className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline">
                  {c.item.kind === 'LOOKUP_TRENDS' ? 'Trends' : 'Google Ads'} · {c.item.title} · open for the full result
                </button>
                <div className="mt-1">
                  {c.item.kind === 'LOOKUP_TRENDS' ? (
                    <TrendPanel trend={c.item.payload.trend as TrendPayload} compact testId={`landed-${c.item.id}`} />
                  ) : (
                    <DemandRowsPanel rows={c.item.payload.rows as DemandRow[]} related={(Array.isArray(c.item.payload.related) ? c.item.payload.related : []) as DemandRow[]} compact testId={`landed-${c.item.id}`} />
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {(lookup || Object.keys(d.payload).length > 0) && (
        <details className="mt-3" open={lookup && !panelled}>
          <summary className="text-xs text-gray-500 cursor-pointer">Payload</summary>
          <PayloadView payload={d.payload} />
        </details>
      )}

      {lookup && d.status === 'DONE' && (
        <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 flex flex-wrap gap-3">
          {d.serp_id && <span>SERP #{d.serp_id}</span>}
          {d.page_id && <span>Page #{d.page_id}</span>}
          {d.keyword_metrics_run_id && <span>Metrics run #{d.keyword_metrics_run_id}</span>}
          {d.trend_snapshot_id && <span>Trends snapshot #{d.trend_snapshot_id}</span>}
        </div>
      )}

      {/* SETTLED QUESTIONS. A persona looked and found nothing, so this hole
          stopped being work. Shown because a judgement nobody can see is a
          judgement nobody can reverse — and new information is exactly when a
          dead end stops being one. */}
      {(d.closures ?? []).length > 0 && (
        <section className="mt-4" data-testid="item-closures">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">
            Settled - nothing more to get
          </h4>
          <ul className="flex flex-col gap-1">
            {(d.closures ?? []).map(c => (
              <li
                key={c.id}
                className="text-xs text-gray-600 dark:text-gray-300 flex items-start gap-1.5"
              >
                <span className="rounded bg-gray-100 dark:bg-gray-700 px-1 shrink-0">
                  {c.what.replace(/_/g, ' ')}
                </span>
                <span className="min-w-0">
                  {c.reason}
                  <span className="text-gray-400">
                    {' '} - {personas.get(c.closed_by_id ?? -1)?.name ?? c.archetype.replace(/_/g, ' ')}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label={`Ask again: ${c.what}`}
                  title="Ask again - someone will look at this once more"
                  onClick={() => reopen(c)}
                  className="ml-auto shrink-0 text-gray-400 hover:text-blue-600"
                >
                  ask again
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-4">
        <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Tags</h4>
        <div className="flex flex-wrap gap-1">
          {d.tags.map(t => (
            <TagChip
              key={t.id}
              dimension={t.dimension}
              value={t.value}
              showDimension
              approved={!!t.approved_at}
              title={
                (t.placed_by_persona_id != null
                  ? `placed by ${personas.get(t.placed_by_persona_id)?.name ?? 'a persona'}`
                  : 'placed by you') + (t.approved_at ? ' · approved' : ' · unverified')
              }
            >
              {!t.approved_at && (
                <button
                  type="button"
                  aria-label={`Approve tag ${t.dimension}:${t.value}`}
                  title="Approve - the receipt says what the tag says"
                  onClick={() => approveTag(t)}
                  className="text-gray-400 hover:text-emerald-600"
                >
                  ✓
                </button>
              )}
              <button
                type="button"
                aria-label={`Remove tag ${t.dimension}:${t.value}`}
                onClick={() => removeTag(t)}
                className="icon-btn text-gray-400 hover:text-red-500"
              >
                ×
              </button>
            </TagChip>
          ))}
          {d.tags.length === 0 && <span className="text-xs text-gray-400">none yet</span>}
        </div>
        <div className="mt-2 flex items-center gap-1">
          <select
            aria-label="Tag dimension"
            value={dimension}
            onChange={e => setDimension(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-1 py-0.5 text-xs"
          >
            {DIMENSIONS.map(x => (
              <option key={x} value={x}>
                {x.toLowerCase().replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <input
            aria-label="Tag value"
            value={value}
            onChange={e => setValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') addTag();
              if (e.key === 'Escape') setValue('');
            }}
            placeholder="value · enter to tag"
            className="flex-1 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
        </div>
      </section>

      {/* SOURCE. What this rests on, by name — the search, the page, the
          numbers, the evidence — stamped by the tool layer when it was made.
          A person's own item needs none; a persona's item with none says so,
          because "no receipt" is a fact about the board worth seeing. */}
      {!lookup && item.kind !== 'VERDICT' && item.kind !== 'CRITIQUE' && (
        <section className="mt-4" data-testid="source">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Source</h4>
          {/* THE WORDS, AND WHETHER THE PAGE SAYS THEM. A plain substring check
              against the text the persona was shown — see boardroom/quotes.py. */}
          {typeof d.payload.quote === 'string' && (
            <div className="mb-1 text-xs" data-testid="quote">
              <span className="text-gray-700 dark:text-gray-200">“{d.payload.quote as string}”</span>{' '}
              {d.payload.quote_verified === true && (
                <span className="text-emerald-700 dark:text-emerald-300">- found in the source</span>
              )}
              {d.payload.quote_verified === false && (
                <span className="text-amber-700 dark:text-amber-300">
 - NOT found in the source it cites
                </span>
              )}
              {d.payload.quote_verified == null && (
                <span className="text-gray-400">- nothing on the board to check it against</span>
              )}
            </div>
          )}
          {/* THE POST, NOT THE SEARCH: the one result, story or post the quote
              is from, so it can be followed back. */}
          {origin && (
            <div className="mb-1 text-xs" data-testid="quote-origin">
              <span className="text-gray-500 dark:text-gray-400">said in </span>
              <a href={origin.url} target="_blank" rel="noreferrer" title={origin.url} className="text-indigo-600 dark:text-indigo-400 hover:underline">
                {originLabel(origin)} ↗
              </a>
            </div>
          )}
          <div className="text-xs text-gray-600 dark:text-gray-300">
            {item.created_by_persona_id == null ? (
              <span>added by you</span>
            ) : (detail?.receipts ?? []).length > 0 ? (
              <>
                <span>found by {whoMade(item, personas)} from </span>
                {(detail?.receipts ?? []).map((r, i) => (
                  <span key={r.id}>
                    {i > 0 && ', '}
                    <button type="button" onClick={() => onOpen(r)} className="text-indigo-600 dark:text-indigo-400 hover:underline text-left" title={`${KIND_LABELS[r.kind]} #${r.id}`}>
                      {receiptLabel(r)}
                    </button>
                  </span>
                ))}
              </>
            ) : (
              <span className="text-amber-700 dark:text-amber-300">
                found by {whoMade(item, personas)} - no receipt: nothing on the board says where this came from
              </span>
            )}
          </div>
        </section>
      )}

      {d.parents.length > 0 && (
        <section className="mt-4">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Came from</h4>
          {d.parents.map(p => (
            <button
              key={`${p.link_kind}-${p.item.id}`}
              type="button"
              onClick={() => onOpen(p.item)}
              className="block text-left text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {p.link_kind.toLowerCase()} · {KIND_LABELS[p.item.kind]} · {p.item.title}
            </button>
          ))}
        </section>
      )}

      {item.kind === 'PRICING' && (
        <section className="mt-3 text-sm font-medium text-orange-700 dark:text-orange-300" data-testid="price-label">
          {priceLabel(d.payload)}
          {typeof d.payload.observed_at === 'string' && (
            <span className="ml-2 text-xs font-normal text-gray-400">seen {d.payload.observed_at as string}</span>
          )}
        </section>
      )}

      {item.kind === 'PRODUCT' && (
        /* PLANS live here, not on the stage form: a plan with no product is
           refused, and the product is the one thing a human has open when
           they know its price. */
        <section className="mt-4" data-testid="plans">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Plans</h4>
          {plans.length === 0 ? (
            <div className="text-xs text-gray-400">no pricing recorded yet</div>
          ) : (
            plans.map(c => (
              <div key={c.item.id} className="text-xs flex items-baseline gap-2">
                <button
                  type="button"
                  onClick={() => onOpen(c.item)}
                  className="text-left text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {c.item.title}
                </button>
                <span className="text-orange-700 dark:text-orange-300">{priceLabel(c.item.payload)}</span>
              </div>
            ))
          )}
          <AddItemForm onAdd={addPlan} parentId={item.id} compact fixedKind="PRICING" />
        </section>
      )}

      {d.children.length > 0 && (
        <section className="mt-4">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Led to</h4>
          {d.children.map(c => (
            <div key={`${c.link_kind}-${c.item.id}`} className="text-xs">
              <button
                type="button"
                onClick={() => onOpen(c.item)}
                className="text-left text-indigo-600 dark:text-indigo-400 hover:underline"
              >
                {c.link_kind.toLowerCase()} · {KIND_LABELS[c.item.kind]} · {c.item.title}
              </button>
              <span className="text-gray-400"> · {statusLabel(c.item.kind, c.item.status)}</span>
              {typeof c.item.payload.url === 'string' && (
                <a href={c.item.payload.url as string} target="_blank" rel="noreferrer" title={c.item.payload.url as string} className="ml-1 text-gray-400 hover:text-indigo-600">
                  ↗
                </a>
              )}
            </div>
          ))}
          {/* Capped: a type can have hundreds under it, and its own page is
              where they are read. */}
          {(detail?.children_total ?? 0) > d.children.length && (
            <p className="mt-1 text-xs text-gray-400" data-testid="drawer-more-children">
              and {((detail?.children_total ?? 0) - d.children.length).toLocaleString()} more
            </p>
          )}
        </section>
      )}

      {item.kind === 'IDEA' && (
        /* THE WAY OUT OF THE ROOM. The brief becomes the subject's description
           and the room's own demand terms become its keywords. */
        <section className="mt-4" data-testid="promote">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Make it a subject</h4>
          <PromoteIdea
            boardroomId={boardroomId}
            ideaId={item.id}
            promotedProjectId={
              typeof d.payload.promoted_project_id === 'number'
                ? (d.payload.promoted_project_id as number)
                : null
            }
            canPublish={canPublish}
          />
        </section>
      )}

      {!lookup && item.kind !== 'VERDICT' && item.kind !== 'CRITIQUE' && (
        <section className="mt-4">
          <h4 className="text-xs uppercase tracking-wide text-gray-400 mb-1">Verdict</h4>
          <div className="flex items-center gap-2 text-xs">
            {d.verdict ? (
              <VerdictBadge verdict={d.verdict.verdict} />
            ) : (
              <span className="text-gray-400">not validated yet</span>
            )}
            <span className="text-gray-300">·</span>
            <button type="button" onClick={() => rule('HOLDS')} className="px-1.5 py-0.5 rounded border border-emerald-300 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
              Confident
            </button>
            <button type="button" onClick={() => rule('DOES_NOT_HOLD')} className="px-1.5 py-0.5 rounded border border-red-300 text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20">
              Not confident
            </button>
            <button type="button" onClick={() => rule('UNVERIFIABLE')} className="px-1.5 py-0.5 rounded border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
              Unverifiable
            </button>
          </div>
        </section>
      )}

      <section className="mt-5 flex items-center gap-2 text-xs">
        {d.status !== 'KILLED' ? (
          <button type="button" onClick={() => setStatus('KILLED')} className="px-2 py-1 rounded border border-amber-300 text-amber-800 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20">
            Rule out
          </button>
        ) : (
          <button type="button" onClick={() => setStatus('OPEN')} className="px-2 py-1 rounded border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
            Reopen
          </button>
        )}
        {!lookup && d.status !== 'DONE' && d.status !== 'KILLED' && (
          <button type="button" onClick={() => setStatus('DONE')} className="px-2 py-1 rounded border border-emerald-300 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/20">
            Mark done
          </button>
        )}
        {lookup && d.status === 'FAILED' && (
          <button type="button" onClick={() => setStatus('OPEN')} className="px-2 py-1 rounded border border-indigo-300 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-900/20">
            Queue again
          </button>
        )}
      </section>
    </Drawer>
  );
}
